import { motion } from 'framer-motion'

const ChartPhoneMockup = () => {
  return (
    <div className="relative">
      <div className="w-80 h-[640px] bg-gray-900 rounded-[3rem] p-3 shadow-2xl border-2 border-gray-700">
        <div className="w-full h-full bg-black rounded-[2.5rem] overflow-hidden border border-gray-800">
          <motion.div 
            className="p-5"
            animate={{ y: [0, -300, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", repeatDelay: 2 }}
          >
            {/* Status Bar */}
            <div className="flex justify-between items-center mb-6 text-sm font-semibold text-white">
              <span>9:41</span>
              <div className="flex gap-1">
                <div className="w-4 h-3 bg-gray-600 rounded-sm"></div>
                <div className="w-4 h-3 bg-gray-600 rounded-sm"></div>
                <div className="w-4 h-3 bg-gray-600 rounded-sm"></div>
              </div>
            </div>

            {/* Header */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="mb-6"
            >
              <div className="text-sm text-gray-400 mb-1">Monatliche Statistiken</div>
              <div className="text-2xl font-bold text-white">Februar 2026</div>
            </motion.div>

            {/* Monthly Spending Chart */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="mb-6"
            >
              <div className="text-xs font-bold text-gray-300 mb-3">Ausgaben pro Monat</div>
              <div className="h-40 relative">
                <svg viewBox="0 0 300 160" className="w-full h-full">
                  <defs>
                    <linearGradient id="chart-gradient-bars" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" style={{ stopColor: '#667EEA', stopOpacity: 0.8 }} />
                      <stop offset="100%" style={{ stopColor: '#764BA2', stopOpacity: 0.6 }} />
                    </linearGradient>
                  </defs>
                  
                  {/* Grid lines */}
                  <line x1="0" y1="40" x2="300" y2="40" stroke="#E5E7EB" strokeWidth="1" />
                  <line x1="0" y1="80" x2="300" y2="80" stroke="#E5E7EB" strokeWidth="1" />
                  <line x1="0" y1="120" x2="300" y2="120" stroke="#E5E7EB" strokeWidth="1" />
                  
                  {/* Bars - Last 6 months */}
                  {/* Sept */}
                  <rect x="10" y="50" width="35" height="110" rx="4" fill="url(#chart-gradient-bars)" opacity="0.4" />
                  <text x="27" y="150" fontSize="10" fill="#6B7280" textAnchor="middle">Sep</text>
                  
                  {/* Oct */}
                  <rect x="55" y="60" width="35" height="100" rx="4" fill="url(#chart-gradient-bars)" opacity="0.5" />
                  <text x="72" y="150" fontSize="10" fill="#6B7280" textAnchor="middle">Okt</text>
                  
                  {/* Nov */}
                  <rect x="100" y="40" width="35" height="120" rx="4" fill="url(#chart-gradient-bars)" opacity="0.6" />
                  <text x="117" y="150" fontSize="10" fill="#6B7280" textAnchor="middle">Nov</text>
                  
                  {/* Dec */}
                  <rect x="145" y="20" width="35" height="140" rx="4" fill="url(#chart-gradient-bars)" opacity="0.7" />
                  <text x="162" y="150" fontSize="10" fill="#6B7280" textAnchor="middle">Dez</text>
                  
                  {/* Jan */}
                  <rect x="190" y="35" width="35" height="125" rx="4" fill="url(#chart-gradient-bars)" opacity="0.8" />
                  <text x="207" y="150" fontSize="10" fill="#6B7280" textAnchor="middle">Jan</text>
                  
                  {/* Feb - Highest (current month) */}
                  <rect x="235" y="10" width="35" height="150" rx="4" fill="url(#chart-gradient-bars)" />
                  <text x="252" y="150" fontSize="10" fill="#667EEA" fontWeight="bold" textAnchor="middle">Feb</text>
                  
                  {/* Amount labels */}
                  <text x="252" y="5" fontSize="11" fill="#667EEA" fontWeight="bold" textAnchor="middle">€1.2k</text>
                </svg>
              </div>
            </motion.div>

            {/* Category Breakdown Pie Chart */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.5 }}
              className="mb-6"
            >
              <div className="text-xs font-bold text-gray-300 mb-3">Ausgabenverteilung</div>
              <div className="flex items-center gap-4">
                {/* Simple Pie Chart representation */}
                <div className="relative w-24 h-24 flex-shrink-0">
                  <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                    {/* Food - 45% */}
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      fill="none"
                      stroke="#F97316"
                      strokeWidth="20"
                      strokeDasharray="113 282"
                      strokeDashoffset="0"
                    />
                    {/* Entertainment - 27% */}
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      fill="none"
                      stroke="#3B82F6"
                      strokeWidth="20"
                      strokeDasharray="68 282"
                      strokeDashoffset="-113"
                    />
                    {/* Transport - 18% */}
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      fill="none"
                      stroke="#10B981"
                      strokeWidth="20"
                      strokeDasharray="45 282"
                      strokeDashoffset="-181"
                    />
                    {/* Others - 10% */}
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      fill="none"
                      stroke="#A855F7"
                      strokeWidth="20"
                      strokeDasharray="28 282"
                      strokeDashoffset="-226"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-xs font-bold text-white">€696</div>
                      <div className="text-xs text-gray-400">Total</div>
                    </div>
                  </div>
                </div>
                
                {/* Legend */}
                <div className="space-y-2 text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                    <span className="text-gray-300">Lebensmittel 45%</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    <span className="text-gray-300">Unterhaltung 27%</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    <span className="text-gray-300">Transport 18%</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                    <span className="text-gray-300">Sonstiges 10%</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Weekly Trend */}
            <div className="mb-4">
              <div className="text-xs font-bold text-gray-300 mb-3">Wöchentlicher Trend</div>
              <div className="h-16 relative">
                <svg viewBox="0 0 280 60" className="w-full h-full">
                  <defs>
                    <linearGradient id="weekly-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" style={{ stopColor: '#10B981', stopOpacity: 0.3 }} />
                      <stop offset="100%" style={{ stopColor: '#10B981', stopOpacity: 0 }} />
                    </linearGradient>
                  </defs>
                  
                  {/* Area chart */}
                  <path
                    d="M 0 45 L 40 35 L 80 40 L 120 25 L 160 30 L 200 20 L 240 15 L 280 10"
                    fill="none"
                    stroke="#10B981"
                    strokeWidth="2"
                  />
                  <path
                    d="M 0 45 L 40 35 L 80 40 L 120 25 L 160 30 L 200 20 L 240 15 L 280 10 L 280 60 L 0 60 Z"
                    fill="url(#weekly-gradient)"
                  />
                  
                  {/* Days labels */}
                  <text x="0" y="55" fontSize="8" fill="#9CA3AF">Mo</text>
                  <text x="40" y="55" fontSize="8" fill="#9CA3AF">Di</text>
                  <text x="80" y="55" fontSize="8" fill="#9CA3AF">Mi</text>
                  <text x="120" y="55" fontSize="8" fill="#9CA3AF">Do</text>
                  <text x="160" y="55" fontSize="8" fill="#9CA3AF">Fr</text>
                  <text x="200" y="55" fontSize="8" fill="#9CA3AF">Sa</text>
                  <text x="240" y="55" fontSize="8" fill="#9CA3AF">So</text>
                </svg>
              </div>
            </div>

            {/* Stats Summary */}
            <div className="grid grid-cols-3 gap-2 pt-4 border-t border-gray-800">
              <div className="text-center">
                <div className="text-xs text-gray-400">Durchschnitt</div>
                <div className="text-sm font-bold text-white">€98/Tag</div>
              </div>
              <div className="text-center">
                <div className="text-xs text-gray-400">Höchste</div>
                <div className="text-sm font-bold text-red-400">€187</div>
              </div>
              <div className="text-center">
                <div className="text-xs text-gray-400">Niedrigste</div>
                <div className="text-sm font-bold text-green-400">€45</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  )
}

export default ChartPhoneMockup
